// Dashboard.jsx

import { Box, Grid, Typography, CircularProgress, alpha, Stack, Button, Skeleton, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Paper, Accordion, AccordionSummary, AccordionDetails, Card, Divider, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme/theme";

import MachineBarChart from "./MachineBarchart";
import ProductionPieChart from "./ProductionPieChart";
import useDashboardData from "./useDashboardData";
import SubscriptionCard from "./SubscriptionCard";

import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StorageIcon from "@mui/icons-material/Storage";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FactoryIcon from "@mui/icons-material/Factory";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useState } from "react";
import CustomTableContainer from "../../../Layout/CustomTableContainer";
import { PieChart } from "recharts";



export default function Dashboard() {

  const { data, loading, fetchdashboarddata, reelSummaryData } = useDashboardData();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  //const [fromDate, setFromDate] = useState(dayjs().subtract(7, "day"));
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [todayData, setTodayData] = useState(true);

  // if (loading || !data) {
  //   return (
  //     <Box p={4} display="flex" justifyContent="center" alignItems="center">
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const startDate = data?.subscription?.start_date
    ? new Date(data.subscription.start_date)
    : null;

  const expiryDate = data?.subscription?.expiry_date
    ? new Date(data.subscription.expiry_date)
    : null;

  let daysLeft = 0;
  let totalDays = 1; // avoid dividing by zero

  if (startDate && expiryDate && !isNaN(startDate) && !isNaN(expiryDate)) {
    const now = new Date();
    daysLeft = Math.max(
      0,
      Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
    );

    totalDays = Math.ceil((expiryDate - startDate) / (1000 * 60 * 60 * 24));
  }



  const fetchDashBoardTopDetails = async () => {
    // Trim to avoid spaces being considered valid
    const from = (fromDate || '');
    const to = (toDate || '');

    // 1. Required checks
    if (!from && !to) {
      alert('Please select both From date and To date.');
      return;
    }

    if (!from) {
      alert('Please select From date.');
      return;
    }

    if (!to) {
      alert('Please select To date.');
      return;
    }

    // 2. Basic date validity checks
    const fromDt = new Date(from);
    const toDt = new Date(to);



    if (isNaN(fromDt.getTime())) {
      alert('From date is not a valid date.');
      return;
    }

    if (isNaN(toDt.getTime())) {
      alert('To date is not a valid date.');
      return;
    }

    // 3. Logical range validation
    if (fromDt > toDt) {
      alert('From date cannot be later than To date.');
      return;
    }

    // 4. Optional: prevent future dates (if your use case requires it)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // if (fromDt > today || toDt > today) {
    //   alert('Dates cannot be in the future.');
    //   return;
    // }

    // 5. If everything is valid, fetch data

    const para_from = dayjs(from).format('YYYY-MM-DD');
    const para_to = dayjs(to).format('YYYY-MM-DD');

    if (para_from === para_to) {
      setTodayData(true);
    }
    else {
      setTodayData(false);
    }
    fetchdashboarddata(para_from, para_to);
  };



  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`,
        minHeight: '100vh',
        p: { xs: 2, md: 3, lg: 4 },
      }}
    >
      {/* HEADER SECTION */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.grey[100],
                mb: 0.5,
                letterSpacing: '-0.8px'
              }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.grey[300], fontSize: '0.95rem' }}
            >
              Real-time overview of your operations
            </Typography>
          </Box>

          {/* DATE PICKER & REFRESH */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={(newVal) => setFromDate(newVal)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: '100%', sm: 140 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.primary[400],
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: colors.primary[300],
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.blueAccent[500],
                          borderWidth: '2px'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: colors.grey[100],
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: colors.grey[300],
                        opacity: 1,
                      }
                    }
                  }
                }}
              />

              <DatePicker
                label="To"
                value={toDate}
                onChange={(newVal) => setToDate(newVal)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: { xs: '100%', sm: 140 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.primary[400],
                        borderRadius: 1.5,
                        '& fieldset': {
                          borderColor: colors.primary[300],
                          borderWidth: '1.5px'
                        },
                        '&:hover fieldset': {
                          borderColor: colors.blueAccent[500],
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.blueAccent[500],
                          borderWidth: '2px'
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: colors.grey[100],
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: colors.grey[300],
                        opacity: 1,
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchDashBoardTopDetails?.()}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                padding: '10px 20px',
                background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[600]} 100%)`,
                boxShadow: `0 4px 12px ${alpha(colors.blueAccent[500], 0.25)}`,
                color: colors.grey[100],
                borderRadius: 1.5,
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                  boxShadow: `0 6px 20px ${alpha(colors.blueAccent[500], 0.35)}`,
                  transform: 'translateY(-2px)',
                }
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      {loading ? (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Subscription Skeleton */}
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
          </Grid>

          {/* Production KPI Cards Skeleton */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {Array(6).fill(0).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height="110px"
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Sales Skeleton */}
          <Grid item xs={12} md={4}>
            <Skeleton
              variant="rectangular"
              height="120px"
              sx={{ borderRadius: 3 }}
            />
          </Grid>

          {/* Warehouse KPI Skeleton */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {Array(3).fill(0).map((_, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Skeleton
                    variant="rectangular"
                    height="120px"
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Charts Skeleton */}
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      ) : (
        <Box>
          {/* SUBSCRIPTION SECTION */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
              <SubscriptionCard data={data} colors={colors} daysLeft={daysLeft} totalDays={totalDays} />
            </Grid>
          </Grid>

          {/* PRODUCTION SECTION */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <FactoryIcon sx={{ color: colors.blueAccent[500], fontSize: '1.8rem' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: colors.grey[100],
                  fontSize: '1.2rem',
                  letterSpacing: '-0.3px'
                }}
              >
                Production Metrics
              </Typography>
              <Divider sx={{ flex: 1, ml: 2, borderColor: colors.primary[300] }} />
            </Stack>

            <Grid container spacing={3}>
              {/* Production KPI Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {[
                    {
                      title: todayData ? "MO Released Today" : "Total MO Released",
                      value: data.production.mo_released_today,
                      bg: colors.primary[400],
                      text: colors.grey[100],
                      icon: '📋'
                    },
                    {
                      title: "Planned Quantity",
                      value: data.production.planned_qty,
                      bg: colors.greenAccent[600],
                      text: colors.grey[900],
                      icon: '📊'
                    },
                    {
                      title: "Produced Parent Reels",
                      value: data.production.produced_reels,
                      bg: colors.orangeAccent[500],
                      text: colors.grey[900],
                      icon: '🎯'
                    },
                    {
                      title: "Produced Child Reels",
                      value: data.production.produced_child_reels,
                      bg: colors.redAccent[400],
                      text: colors.grey[900],
                      icon: '🔄'
                    },
                    {
                      title: "Produced Reams",
                      value: data.production.produced_reams,
                      bg: colors.purpleAccent[500],
                      text: colors.grey[100],
                      icon: '📦'
                    },
                    {
                      title: "Total Bundles",
                      value: data.production.produced_bundle,
                      bg: colors.blueAccent[400],
                      text: colors.grey[900],
                      icon: '📚'
                    },
                    {
                      title: "Total Ream Pallet",
                      value: data.production.produced_ream_pallet,
                      bg: colors.yellowAccent[600],
                      text: colors.grey[900],
                      icon: '🏭'
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={index}>
                      <Card
                        sx={{
                          backgroundColor: item.bg,
                          color: item.text,
                          borderRadius: 2.5,
                          padding: 2.5,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                          height: "120px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          border: 'none',
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          position: 'relative',
                          overflow: 'hidden',
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                          },
                          "&::before": {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.3), transparent)',
                          }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                opacity: 0.9,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mb: 1
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 800,
                                letterSpacing: 0.5,
                                fontSize: '2rem'
                              }}
                            >
                              {item.value ?? 0}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '2rem' }}>
                            {item.icon}
                          </Typography>
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>


          {/* SALES & WAREHOUSE SECTION */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LocalShippingIcon sx={{ color: colors.blueAccent[500], fontSize: '1.8rem' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: colors.grey[100],
                  fontSize: '1.2rem',
                  letterSpacing: '-0.3px'
                }}
              >
                Sales & Warehouse Overview
              </Typography>
              <Divider sx={{ flex: 1, ml: 2, borderColor: colors.primary[300] }} />
            </Stack>

            <Grid container spacing={3}>
              {/* Sales KPI */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Card
                  sx={{
                    backgroundColor: colors.redAccent[500],
                    color: colors.grey[100],
                    borderRadius: 2.5,
                    padding: 2.5,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    height: "130px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    border: 'none',
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    position: 'relative',
                    overflow: 'hidden',
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                    },
                    "&::before": {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0.3), transparent)',
                    }
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 1
                        }}
                      >
                        {todayData ? "New Orders Today" : "Total Orders"}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2rem' }}>
                        {data.sales.new_orders_today ?? 0}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '2rem' }}>📦</Typography>
                  </Stack>
                </Card>
              </Grid>

              {/* Warehouse KPI Boxes */}
              <Grid item xs={12} sm={6} md={8} lg={9} xl={9.6}>
                <Grid container spacing={2}>
                  {/* Reels Received */}
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                    <Card
                      sx={{
                        backgroundColor: colors.blueAccent[500],
                        color: colors.grey[100],
                        borderRadius: 2.5,
                        padding: 2.5,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        height: "130px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        border: 'none',
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                        },
                        "&::before": {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.3), transparent)',
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 1
                            }}
                          >
                            {todayData ? "Reels Received Today" : "Reels Received"}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2rem' }}>
                            {data.warehouse.reels_received ?? 0}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '2rem' }}>📥</Typography>
                      </Stack>
                    </Card>
                  </Grid>

                  {/* Bundles Received */}
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                    <Card
                      sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: colors.grey[900],
                        borderRadius: 2.5,
                        padding: 2.5,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        height: "130px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        border: 'none',
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                        },
                        "&::before": {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.3), transparent)',
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 1
                            }}
                          >
                            {todayData ? "Bundles Received Today" : "Bundles Received"}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2rem' }}>
                            {data.warehouse.bundles_received ?? 0}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '2rem' }}>📦</Typography>
                      </Stack>
                    </Card>
                  </Grid>

                  {/* Ream Pallets */}
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                    <Card
                      sx={{
                        backgroundColor: colors.primary[400],
                        color: colors.grey[100],
                        borderRadius: 2.5,
                        padding: 2.5,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        height: "130px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        border: 'none',
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        position: 'relative',
                        overflow: 'hidden',
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                        },
                        "&::before": {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: 'linear-gradient(90deg, rgba(255,255,255,0.3), transparent)',
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 1
                            }}
                          >
                            {todayData ? "Ream Pallets Today" : "Ream Pallets Received"}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2rem' }}>
                            {data.warehouse.ream_pallet_received ?? 0}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '2rem' }}>🏗️</Typography>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>



          {/* ANALYTICS SECTION */}
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ color: colors.blueAccent[500], fontSize: '1.8rem' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: colors.grey[100],
                  fontSize: '1.2rem',
                  letterSpacing: '-0.3px'
                }}
              >
                Analytics & Reports
              </Typography>
              <Divider sx={{ flex: 1, ml: 2, borderColor: colors.primary[300] }} />
            </Stack>

            <Grid container spacing={3}>
              {/* Child Reel Summary */}
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Card
                  sx={{
                    backgroundColor: colors.greenAccent[800],
                    borderRadius: 2.5,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    '&:hover': {
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-4px)',
                    }
                  }}
                >
                  <Accordion
                    sx={{ backgroundColor: 'transparent' }}
                    defaultExpanded
                  >
                    <AccordionSummary sx={{ fontWeight: 700, color: colors.grey[100] }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        Child Reel Summary<br />
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {reelSummaryData.length} grades
                        </Typography>
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Table size="small">
                        <CustomTableContainer colors={colors}>
                          <TableContainer>
                            <Table>
                              <TableHead sx={{ backgroundColor: colors.primary[400] }}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700, color: colors.grey[100] }}>Grade</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: colors.grey[100] }}>Count</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {reelSummaryData.map((s) => (
                                  <TableRow key={s.grade} sx={{ '&:hover': { backgroundColor: alpha(colors.primary[400], 0.2) } }}>
                                    <TableCell sx={{ fontWeight: 500 }}>{s.grade}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{s.reelcount}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CustomTableContainer>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                </Card>
              </Grid>

              {/* Charts Grid */}
              <Grid item xs={12} sm={6} md={8} lg={9} xl={9.6}>
                <Grid container spacing={2}>
                  {/* Production Pie Chart */}
                  <Grid item xs={12} md={6} lg={5}>
                    <Card
                      sx={{
                        backgroundColor: colors.primary[400],
                        borderRadius: 2.5,
                        padding: 2.5,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        border: 'none',
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                          transform: 'translateY(-4px)',
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: colors.grey[100] }}>
                        Production Distribution
                      </Typography>
                      <ProductionPieChart production={data.production} todayData={todayData} />
                    </Card>
                  </Grid>

                  {/* Machine Bar Chart */}
                  <Grid item xs={12} md={6} lg={7}>
                    <Card
                      sx={{
                        backgroundColor: colors.primary[400],
                        borderRadius: 2.5,
                        padding: 2.5,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        border: 'none',
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                          transform: 'translateY(-4px)',
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: colors.grey[100] }}>
                        Machine Performance
                      </Typography>
                      <MachineBarChart data={data.machine_production} todayData={todayData} />
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
}
